import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from "recharts";
import { getProductPopularity } from "@/lib/analyticsDB";
import type { RecommendationRecord } from "@/lib/analyticsDB";

const COLORS = ["#10b981", "#34d399", "#059669", "#6ee7b7", "#14b8a6", "#0ea5e9", "#8b5cf6", "#f59e0b", "#ef4444", "#ec4899", "#84cc16", "#f97316", "#06b6d4", "#a855f7"];

export default function ProductPopularity({ recs }: { recs: RecommendationRecord[] }) {
  const sorted = getProductPopularity(recs);
  const data = Array.from(sorted.entries()).map(([product, count]) => ({
    product,
    count,
    label: product.replace("StackMap ", ""),
  }));

  const total = data.reduce((sum, d) => sum + d.count, 0);

  return (
    <div className="glass-card p-4 md:p-5">
      <div className="mb-5">
        <h3 className="text-base font-bold text-white">Most Sought-After Products</h3>
        <p className="text-xs text-silver-400 mt-0.5">
          Which StackMap products are recommended the most often
        </p>
      </div>

      <div className="w-full" style={{ height: 280 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} layout="vertical" margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#2e2e3e" horizontal={false} />
            <XAxis type="number" tick={{ fill: "#9898b0", fontSize: 11 }} axisLine={false} tickLine={false} />
            <YAxis
              type="category"
              dataKey="label"
              tick={{ fill: "#c8c8d8", fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              width={100}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: "#161622",
                border: "1px solid #2e2e3e",
                borderRadius: 8,
                color: "#e2e2ea",
                fontSize: 12,
              }}
              formatter={(value: number) => [`${value} recommendations`, "Count"]}
            />
            <Bar dataKey="count" radius={[0, 4, 4, 0]} maxBarSize={28}>
              {data.map((_, index) => (
                <Cell key={index} fill={COLORS[index % COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="mt-4 space-y-2">
        {data.slice(0, 5).map((d, i) => {
          const pct = total > 0 ? Math.round((d.count / total) * 100) : 0;
          return (
            <div key={d.product} className="flex items-center gap-3">
              <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
              <span className="text-sm text-silver-200 flex-1">{d.product}</span>
              <span className="text-xs font-medium text-white">{d.count}</span>
              <span className="text-xs text-silver-400 w-8 text-right">{pct}%</span>
              <div className="w-20 h-1.5 bg-graphite-700 rounded-full overflow-hidden flex-shrink-0">
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${pct}%`,
                    backgroundColor: COLORS[i % COLORS.length],
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}